import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { useCheckTypes, useWorkflowStatus } from '../hooks/useMasterData';
import AdvancedFilterDrawer from '../components/AdvancedFilterDrawer';
import MonthYearPicker from '../components/MonthYearPicker';
import SearchableDropdown from '../components/SearchableDropdown';
import ColumnSelector from '../components/ColumnSelector';
import Tooltip from '../components/Tooltip';
import UserTimestamp from '../components/UserTimestamp';
import BulkActionModal from '../components/BulkActionModal';
import { Info, Copy, RotateCcw } from 'lucide-react';
import { formatDateUS, formatDateRange, getCurrentMonthYear, formatMonthYear, getMonthRange } from '../utils/dateUtils';
import { filterEmojis } from '../utils/emojiFilter';
import { loadVisibleColumns, persistVisibleColumns } from '../utils/columnPreferences';
import { useResizableTableColumns } from '../hooks/useResizableTableColumns';
import { CellEllipsis } from '../components/CellEllipsis';
import { ResizableTh } from '../components/ResizableTableHeader';
import './Checks.css';

const UNKNOWN_CHECKS_COLUMN_PREFS_KEY = 'unknownChecksTable';
const UNKNOWN_TABLE_WIDTHS_KEY = 'unknownChecksTableColWidths';
const UNKNOWN_DEFAULT_COL_WIDTH_PX = {
  __checkbox__: 44,
  depositDate: 92,
  checkNumber: 120,
  altCheckNumber: 110,
  payer: 130,
  batchDescription: 140,
  exchange: 90,
  checkType: 110,
  practiceCode: 100,
  locationCode: 100,
  totalAmount: 104,
  postedAmount: 100,
  remainingAmount: 104,
  status: 110,
  workflowStatus: 120,
  assigneeName: 120,
  reporterName: 120,
  createdAt: 118,
  updatedAt: 118
};
const UNKNOWN_LOAD_KEYS = new Set([
  // See Checks.js: 'checkbox' must not be a persisted data column (only __checkbox__ col exists).
  'depositDate',
  'checkNumber',
  'altCheckNumber',
  'payer',
  'batchDescription',
  'exchange',
  'checkType',
  'practiceCode',
  'locationCode',
  'totalAmount',
  'postedAmount',
  'remainingAmount',
  'status',
  'workflowStatus',
  'assigneeName',
  'reporterName',
  'createdAt',
  'updatedAt'
]);
const UNKNOWN_LOAD_FALLBACK_DEFAULTS = [
  'depositDate',
  'checkNumber',
  'payer',
  'exchange',
  'totalAmount',
  'postedAmount',
  'remainingAmount',
  'status',
  'updatedAt'
];

const Unknown = () => {
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: checkTypes } = useCheckTypes(true);
  const { data: workflowStatuses } = useWorkflowStatus(true);
  
  // State management
  const [checks, setChecks] = useState([]);
  const [allChecks, setAllChecks] = useState([]); // Store all fetched checks before filtering
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Storage key for persisting filters
  const STORAGE_KEY = 'unknownFiltersState';

  // Helper function to load filters from sessionStorage (called once during initialization)
  const loadInitialFilters = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading filters from sessionStorage:', error);
    }
    return null;
  };

  const initialFilters = loadInitialFilters();

  // Pagination - initialize from storage if available
  const [currentPage, setCurrentPage] = useState(initialFilters?.currentPage ?? 0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Default filters - initialize from storage if available
  const [searchTerm, setSearchTerm] = useState(initialFilters?.searchTerm ?? '');
  const [selectedStatus, setSelectedStatus] = useState(initialFilters?.selectedStatus ?? '');
  const [selectedPractice, setSelectedPractice] = useState(initialFilters?.selectedPractice ?? '');
  const [practices, setPractices] = useState([]);
  
  // Advanced filters - now support arrays for multiple selections
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const defaultAdvancedFilters = {
    statuses: [], // Array for multiple status selections
    excludeStatuses: [],
    practiceCodes: [], // Array for multiple practice selections
    excludePracticeCodes: [],
    locationCodes: [], // Array for multiple location selections
    excludeLocationCodes: [],
    assigneeIds: [], // Array for multiple assignee selections
    excludeAssigneeIds: [],
    reporterIds: [], // Array for multiple reporter selections
    excludeReporterIds: [],
    checkTypes: [], // Array for multiple check type selections
    excludeCheckTypes: [],
    workflowStatuses: [], // Array for multiple workflow status selections
    excludeWorkflowStatuses: [],
    checkNumber: '',
    payer: '',
    batchNumber: '',
    depositDateFrom: '',
    depositDateTo: '',
    receivedDateFrom: '',
    receivedDateTo: '',
    completedDateFrom: '',
    completedDateTo: '',
    startDate: '',
    endDate: '',
    month: null,
    year: null
  };
  const [advancedFilters, setAdvancedFilters] = useState(
    initialFilters?.advancedFilters ?? defaultAdvancedFilters
  );
  
  // Sorting - initialize from storage if available
  const [sortField, setSortField] = useState(initialFilters?.sortField ?? 'depositDate');
  const [sortDirection, setSortDirection] = useState(initialFilters?.sortDirection ?? 'desc'); // Default: newest first
  
  // Bulk actions
  const [selectedChecks, setSelectedChecks] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkUpdateMessage, setBulkUpdateMessage] = useState(null);
  const [bulkUpdateMessageType, setBulkUpdateMessageType] = useState('success');
  const { users, getUserName, getUserById } = useUsers(); // Get users from context

  // Column selector
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorTriggerRef = useRef(null);

  // Hide pagination during filter changes to prevent flickering
  const [showPagination, setShowPagination] = useState(true);
  
  // Available columns - defined as constant array
  const allColumns = [
    { key: 'checkbox', label: 'Select', alwaysVisible: true },
    { key: 'depositDate', label: 'Deposit Date' },
    { key: 'checkNumber', label: 'Check Number' },
    { key: 'altCheckNumber', label: 'Alt. Check Number' },
    { key: 'payer', label: 'Payer' },
    { key: 'batchDescription', label: 'Batch Description' },
    { key: 'exchange', label: 'Exchange' },
    { key: 'checkType', label: 'Check Type' },
    { key: 'practiceCode', label: 'Practice Code' },
    { key: 'locationCode', label: 'Location Code' },
    { key: 'totalAmount', label: 'Total Amount' },
    { key: 'postedAmount', label: 'Posted' },
    { key: 'remainingAmount', label: 'Remaining' },
    { key: 'status', label: 'Status' },
    { key: 'workflowStatus', label: 'Workflow Status' },
    { key: 'assigneeName', label: 'Assignee Name' },
    { key: 'reporterName', label: 'Reporter Name' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'updatedAt', label: 'Updated At' }
  ];

  // Default visible columns (exclude altCheckNumber, checkType, practiceCode, locationCode, batchDescription)
  const defaultColumns = [
    'depositDate',
    'checkNumber',
    'payer',
    'exchange',
    'totalAmount',
    'postedAmount',
    'remainingAmount',
    'status',
    'updatedAt'
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    loadVisibleColumns(UNKNOWN_CHECKS_COLUMN_PREFS_KEY, UNKNOWN_LOAD_KEYS, UNKNOWN_LOAD_FALLBACK_DEFAULTS)
  );

  useEffect(() => {
    setVisibleColumns((prev) => {
      const next = prev.filter((c) => c !== 'checkbox');
      if (next.length === prev.length) return prev;
      persistVisibleColumns(UNKNOWN_CHECKS_COLUMN_PREFS_KEY, next);
      return next;
    });
  }, []);

  const tableVisibleColumns = useMemo(
    () => visibleColumns.filter((k) => k !== 'checkbox'),
    [visibleColumns]
  );

  const unknownColumnOrder = useMemo(
    () => ['__checkbox__', ...tableVisibleColumns],
    [tableVisibleColumns]
  );
  const { Colgroup, onResizeMouseDown, resetColumnWidths } = useResizableTableColumns({
    storageKey: UNKNOWN_TABLE_WIDTHS_KEY,
    columnIds: unknownColumnOrder,
    defaultWidths: UNKNOWN_DEFAULT_COL_WIDTH_PX,
    minWidth: 40
  });

  // Month/Year selection - initialize from storage or defaults
  const currentMonthYear = getCurrentMonthYear();
  const [selectedMonth, setSelectedMonth] = useState(
    initialFilters?.selectedMonth ?? currentMonthYear.month
  );
  const [selectedYear, setSelectedYear] = useState(
    initialFilters?.selectedYear ?? currentMonthYear.year
  );
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  

  const handleMonthYearSelect = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setShowMonthPicker(false);
  };

  // Function to save filters to sessionStorage
  const saveFiltersToStorage = useCallback(() => {
    try {
      const filterState = {
        searchTerm,
        selectedStatus,
        selectedPractice,
        advancedFilters,
        selectedMonth,
        selectedYear,
        sortField,
        sortDirection,
        currentPage
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filterState));
    } catch (error) {
      console.error('Error saving filters to sessionStorage:', error);
    }
  }, [searchTerm, selectedStatus, selectedPractice, advancedFilters, selectedMonth, selectedYear, sortField, sortDirection, currentPage]);

  const goToUnknownCheckDetails = useCallback(
    (check) => {
      if (!check?.checkId) return;
      const checkIds = checks.map((c) => c.checkId);
      const completeFilters = {
        searchTerm,
        selectedStatus,
        selectedPractice,
        advancedFilters,
        selectedMonth,
        selectedYear,
        sortField,
        sortDirection,
        currentPage,
        page: currentPage,
        size: 50,
        unknown: true
      };
      sessionStorage.setItem('checksNavigationIds', JSON.stringify(checkIds));
      sessionStorage.setItem('checksNavigationFilters', JSON.stringify(completeFilters));
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(completeFilters));
      saveFiltersToStorage();
      navigate(`/checks/${check.checkId}?source=unknown`);
    },
    [
      checks,
      searchTerm,
      selectedStatus,
      selectedPractice,
      advancedFilters,
      selectedMonth,
      selectedYear,
      sortField,
      sortDirection,
      currentPage,
      navigate,
      saveFiltersToStorage
    ]
  );

  const handleCopyCheckNumber = useCallback(
    async (checkNumber) => {
      if (!checkNumber) return;
      try {
        await navigator.clipboard.writeText(checkNumber);
        showSuccess('Check number copied');
      } catch (err) {
        console.error('Copy failed:', err);
        showError('Could not copy check number');
      }
    },
    [showSuccess, showError]
  );

  // Save filters to sessionStorage whenever they change (skip initial mount to avoid overwriting)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip saving on initial mount
    }
    saveFiltersToStorage();
  }, [saveFiltersToStorage]);

  // Restore filters when navigating back to /unknown page
  const hasRestoredFromStorage = useRef(false);
  const isRestoringFromStorage = useRef(false);
  useEffect(() => {
    if (location.pathname === '/unknown' && !hasRestoredFromStorage.current) {
      const stored = loadInitialFilters();
      if (stored) {
        isRestoringFromStorage.current = true;
        // Keep pagination visible when restoring from storage
        setShowPagination(true);
        // Restore page first (before filters) to prevent reset effect from interfering
        if (stored.currentPage !== undefined && stored.currentPage !== currentPage) {
          setCurrentPage(stored.currentPage);
        }
        // Restore filters - the reset effect will be skipped due to isRestoringFromStorage flag
        if (stored.searchTerm !== searchTerm) setSearchTerm(stored.searchTerm || '');
        if (stored.selectedStatus !== selectedStatus) setSelectedStatus(stored.selectedStatus || '');
        if (stored.selectedPractice !== selectedPractice) setSelectedPractice(stored.selectedPractice || '');
        if (JSON.stringify(stored.advancedFilters) !== JSON.stringify(advancedFilters)) {
          setAdvancedFilters(stored.advancedFilters || defaultAdvancedFilters);
        }
        if (stored.selectedMonth !== selectedMonth) setSelectedMonth(stored.selectedMonth ?? currentMonthYear.month);
        if (stored.selectedYear !== selectedYear) setSelectedYear(stored.selectedYear ?? currentMonthYear.year);
        if (stored.sortField !== sortField) setSortField(stored.sortField || 'depositDate');
        if (stored.sortDirection !== sortDirection) setSortDirection(stored.sortDirection || 'desc');
        // Clear the flag after all restorations are queued
        setTimeout(() => {
          isRestoringFromStorage.current = false;
        }, 0);
      }
      hasRestoredFromStorage.current = true;
    }
    // Reset the flag when navigating away
    if (location.pathname !== '/unknown') {
      hasRestoredFromStorage.current = false;
      isRestoringFromStorage.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Fetch practices on mount
  useEffect(() => {
    fetchPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPractices = async () => {
    try {
      const response = await api.getAllPractices();
      const practicesList = Array.isArray(response) ? response : (response?.items || []);
      setPractices(practicesList.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
    }
  };

  const fetchChecks = useCallback(async (page = 0) => {
    // Set loading state but keep old data visible to prevent flickering
    setLoading(true);
    setError(null);
    
    try {
      // Build search parameters for POST request
      const searchParams = {
        page: page,
        size: 50,
        unknown: true  // Always true for Unknown page
      };

      // Collect statuses from both default and advanced filters
      const statuses = [];
      if (selectedStatus) {
        statuses.push(selectedStatus);
      }
      if (advancedFilters.statuses && advancedFilters.statuses.length > 0) {
        statuses.push(...advancedFilters.statuses);
      }
      // Remove duplicates
      if (statuses.length > 0) {
        searchParams.statuses = [...new Set(statuses)];
      }
      if (advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0) {
        searchParams.excludeStatuses = [...new Set(advancedFilters.excludeStatuses)];
      }

      // Collect practice codes
      const practiceCodes = [];
      if (selectedPractice) {
        practiceCodes.push(selectedPractice);
      }
      if (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) {
        practiceCodes.push(...advancedFilters.practiceCodes);
      }
      // Remove duplicates
      if (practiceCodes.length > 0) {
        searchParams.practiceCodes = [...new Set(practiceCodes)];
      }
      if (advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0) {
        searchParams.excludePracticeCodes = [...new Set(advancedFilters.excludePracticeCodes)];
      }

      // Location codes (array)
      if (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) {
        searchParams.locationCodes = [...new Set(advancedFilters.locationCodes)];
      }
      if (advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0) {
        searchParams.excludeLocationCodes = [...new Set(advancedFilters.excludeLocationCodes)];
      }

      // Assignee IDs (array)
      if (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) {
        searchParams.assigneeIds = [...new Set(advancedFilters.assigneeIds)];
      }
      if (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) {
        searchParams.excludeAssigneeIds = [...new Set(advancedFilters.excludeAssigneeIds)];
      }

      // Reporter IDs (array)
      if (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) {
        searchParams.reporterIds = [...new Set(advancedFilters.reporterIds)];
      }
      if (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) {
        searchParams.excludeReporterIds = [...new Set(advancedFilters.excludeReporterIds)];
      }

      // Check types (array)
      if (advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0) {
        searchParams.checkTypes = [...new Set(advancedFilters.checkTypes)];
      }
      if (advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0) {
        searchParams.excludeCheckTypes = [...new Set(advancedFilters.excludeCheckTypes)];
      }

      // Workflow Statuses (array)
      if (advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0) {
        searchParams.workflowStatuses = [...new Set(advancedFilters.workflowStatuses)];
      }
      if (advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0) {
        searchParams.excludeWorkflowStatuses = [...new Set(advancedFilters.excludeWorkflowStatuses)];
      }

      // Check number (supports wildcards)
      // If user enters text without *, it's exact match
      // If user enters text with *, it's regex/wildcard match
      if (advancedFilters.checkNumber && advancedFilters.checkNumber.trim() !== '') {
        searchParams.checkNumber = advancedFilters.checkNumber.trim().toUpperCase();
      } else if (searchTerm && searchTerm.trim() !== '') {
        // Use searchTerm as checkNumber if no explicit checkNumber filter
        searchParams.checkNumber = searchTerm.trim().toUpperCase();
      }
      
      // Payer (supports wildcards)
      // If user enters text without *, it's exact match
      // If user enters text with *, it's regex/wildcard match
      if (advancedFilters.payer && advancedFilters.payer.trim() !== '') {
        searchParams.payer = advancedFilters.payer.trim();
      }

      // Batch number (supports wildcards)
      if (advancedFilters.batchNumber && advancedFilters.batchNumber.trim() !== '') {
        searchParams.batchNumber = advancedFilters.batchNumber.trim().toUpperCase();
      }

      // Date filters - map to new field names
      // Check if user has set any date filters
      const hasDepositDateFilter = advancedFilters.depositDateFrom || advancedFilters.depositDateTo;
      const hasReceivedDateFilter = advancedFilters.receivedDateFrom || advancedFilters.receivedDateTo;
      const hasCompletedDateFilter = advancedFilters.completedDateFrom || advancedFilters.completedDateTo;
      
      // Check if any advanced filters are active
      const isAdvancedFiltersActive = (
        (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
        (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
        (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
        (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
        (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
        (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) ||
        (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) ||
        (advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0) ||
        (advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0) ||
        (advancedFilters.checkNumber && advancedFilters.checkNumber.trim() !== '') ||
        (advancedFilters.payer && advancedFilters.payer.trim() !== '') ||
        (advancedFilters.batchNumber && advancedFilters.batchNumber.trim() !== '') ||
        hasDepositDateFilter ||
        hasReceivedDateFilter ||
        hasCompletedDateFilter
      );
      
      // Get month range for default deposit date (only if no advanced filters are active)
      const monthRange = getMonthRange(selectedMonth, selectedYear);
      
      // Only add deposit date if:
      // 1. User explicitly set deposit date filters, OR
      // 2. No advanced filters are active at all (use default month range)
      if (hasDepositDateFilter) {
        if (advancedFilters.depositDateFrom) {
          searchParams.depositDateFrom = advancedFilters.depositDateFrom;
        }
        if (advancedFilters.depositDateTo) {
          searchParams.depositDateTo = advancedFilters.depositDateTo;
        }
      } else if (!isAdvancedFiltersActive) {
        // Only use default month range if NO advanced filters are active
        searchParams.depositDateFrom = monthRange.startDate;
        searchParams.depositDateTo = monthRange.endDate;
      }
      
      // Received date filters - only add if user explicitly set them
      if (advancedFilters.receivedDateFrom) {
        searchParams.receivedDateFrom = advancedFilters.receivedDateFrom;
      }
      if (advancedFilters.receivedDateTo) {
        searchParams.receivedDateTo = advancedFilters.receivedDateTo;
      }
      
      // Completed date filters - only add if user explicitly set them
      if (advancedFilters.completedDateFrom) {
        searchParams.completedDateFrom = advancedFilters.completedDateFrom;
      }
      if (advancedFilters.completedDateTo) {
        searchParams.completedDateTo = advancedFilters.completedDateTo;
      }

      // Legacy date filters mapping
      if (advancedFilters.startDate && !searchParams.depositDateFrom) {
        searchParams.depositDateFrom = advancedFilters.startDate;
      }
      if (advancedFilters.endDate && !searchParams.depositDateTo) {
        searchParams.depositDateTo = advancedFilters.endDate;
      }

      // Use POST endpoint with search
      const response = await api.searchChecksDashboard(searchParams);
      
      let checksList = response.items || [];
      const totalElements = response.totalElements || 0;
      
      // Client-side sorting (if backend doesn't support it)
      if (sortField && checksList.length > 0) {
        checksList = [...checksList].sort((a, b) => {
          let aVal = a[sortField];
          let bVal = b[sortField];
          
          if (sortField === 'depositDate') {
            aVal = aVal ? new Date(aVal) : new Date(0);
            bVal = bVal ? new Date(bVal) : new Date(0);
          } else if (sortField === 'createdAt' || sortField === 'updatedAt') {
            aVal = aVal ? new Date(aVal) : new Date(0);
            bVal = bVal ? new Date(bVal) : new Date(0);
          } else if (sortField === 'totalAmount' || sortField === 'postedAmount' || sortField === 'remainingAmount') {
            aVal = aVal || 0;
            bVal = bVal || 0;
          } else {
            aVal = aVal || '';
            bVal = bVal || '';
          }
          
          if (sortDirection === 'asc') {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          } else {
            return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
          }
        });
      }
      
      // Update all state at once and show pagination after data loads
      setAllChecks(checksList);
      setTotalElements(totalElements);
      setHasMore(page < (response.totalPages || 0) - 1);
      setCurrentPage(page);
      setLoading(false);
      // Show pagination after data is loaded
      setShowPagination(true);
      
    } catch (err) {
      console.error('Error fetching unknown checks:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to load unknown checks. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
      setLoading(false);
    }
  }, [selectedStatus, selectedPractice, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear, searchTerm, showError]);

  // Fetch checks when filters change (reset to page 0)
  // Skip reset if we're restoring from storage to avoid flickering
  // Note: sortField and sortDirection are intentionally excluded from dependencies
  // to prevent resetting to page 0 when user is sorting on pages 2-30 (fixes flickering)
  // Track previous filter values to detect changes
  const prevFiltersRef = useRef({
    selectedStatus,
    selectedPractice,
    advancedFilters: JSON.stringify(advancedFilters),
    selectedMonth,
    selectedYear
  });

  // Fetch checks when page or filters change (excluding searchTerm - handled client-side)
  // Reset to page 0 when filters change, but not when only page changes
  useEffect(() => {
    const filtersChanged = 
      prevFiltersRef.current.selectedStatus !== selectedStatus ||
      prevFiltersRef.current.selectedPractice !== selectedPractice ||
      prevFiltersRef.current.advancedFilters !== JSON.stringify(advancedFilters) ||
      prevFiltersRef.current.selectedMonth !== selectedMonth ||
      prevFiltersRef.current.selectedYear !== selectedYear;

    if (filtersChanged && !isRestoringFromStorage.current) {
      // Hide pagination immediately when filters change to prevent flickering
      setShowPagination(false);
      // Reset to page 0 when filters change
      if (currentPage !== 0) {
        setCurrentPage(0);
        // Update ref for next comparison
        prevFiltersRef.current = {
          selectedStatus,
          selectedPractice,
          advancedFilters: JSON.stringify(advancedFilters),
          selectedMonth,
          selectedYear
        };
        return; // Don't fetch yet, let the page reset trigger the fetch
      }
    }

    // Update ref for next comparison
    prevFiltersRef.current = {
      selectedStatus,
      selectedPractice,
      advancedFilters: JSON.stringify(advancedFilters),
      selectedMonth,
      selectedYear
    };

    // Fetch data
    fetchChecks(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedStatus, selectedPractice, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear]);


  const handlePreviousPage = () => {
    if (currentPage > 0 && !loading) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore && !loading) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Calculate pagination display values
  const pageSize = 50;
  // If search is active, show filtered results count, otherwise show total from API
  const displayTotal = searchTerm ? checks.length : totalElements;
  const startIndex = searchTerm ? 1 : (currentPage * pageSize + 1);
  const endIndex = searchTerm ? checks.length : Math.min((currentPage + 1) * pageSize, totalElements);

  // Client-side filtering for search term (check number, payer, amount)
  useEffect(() => {
    if (!searchTerm) {
      setChecks(allChecks);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = allChecks.filter(check => {
      // Search by check number
      if (check.checkNumber && check.checkNumber.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by payer
      if (check.payer && check.payer.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by amount (totalAmount, postedAmount, remainingAmount)
      const totalAmount = String(check.totalAmount || '');
      const postedAmount = String(check.postedAmount || '');
      const remainingAmount = String(check.remainingAmount || '');
      
      if (totalAmount.includes(searchLower) || 
          postedAmount.includes(searchLower) || 
          remainingAmount.includes(searchLower)) {
        return true;
      }
      
      return false;
    });

    setChecks(filtered);
  }, [searchTerm, allChecks]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleApplyAdvancedFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetAdvancedFilters = () => {
    setAdvancedFilters({
      statuses: [],
      excludeStatuses: [],
      practiceCodes: [],
      excludePracticeCodes: [],
      locationCodes: [],
      excludeLocationCodes: [],
      assigneeIds: [],
      excludeAssigneeIds: [],
      reporterIds: [],
      excludeReporterIds: [],
      checkTypes: [],
      excludeCheckTypes: [],
      workflowStatuses: [],
      excludeWorkflowStatuses: [],
      checkNumber: '',
      payer: '',
      batchNumber: '',
      depositDateFrom: '',
      depositDateTo: '',
      receivedDateFrom: '',
      receivedDateTo: '',
      completedDateFrom: '',
      completedDateTo: '',
      startDate: '',
      endDate: '',
      month: null,
      year: null
    });
  };

  const handleClearAllFilters = () => {
    // Reset advanced filters
    handleResetAdvancedFilters();
    // Reset basic filters
    setSelectedStatus('');
    setSelectedPractice('');
    setSearchTerm('');
    // Close advanced filter drawer
    setShowAdvancedFilters(false);
    // Clear from storage
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const handleSelectCheck = (checkId) => {
    setSelectedChecks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(checkId)) {
        newSet.delete(checkId);
      } else {
        newSet.add(checkId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedChecks.size === checks.length) {
      setSelectedChecks(new Set());
    } else {
      setSelectedChecks(new Set(checks.map(c => c.checkId)));
    }
  };

  const handleBulkUpdate = async (payload) => {
    if (selectedChecks.size === 0) return;
    
    try {
      const checkIds = Array.from(selectedChecks);
      const response = await api.bulkUpdateChecks({
        checkIds,
        ...payload
      });
      
      // Show success message from API response
      if (response) {
        setBulkUpdateMessage(response.message || `Successfully updated ${response.succeeded || checkIds.length} check(s)`);
        setBulkUpdateMessageType(response.failed > 0 ? 'warning' : 'success');
        
        // Auto-hide message after 5 seconds
        setTimeout(() => {
          setBulkUpdateMessage(null);
        }, 5000);
      }
      
      // Refresh checks
      await fetchChecks(currentPage);
      setSelectedChecks(new Set());
      setShowBulkActions(false);
      setShowBulkActionModal(false);
    } catch (error) {
      console.error('Error bulk updating checks:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to update checks. Please try again.';
      setBulkUpdateMessage(errorMessage);
      showError(errorMessage);
      setBulkUpdateMessageType('error');
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setBulkUpdateMessage(null);
      }, 5000);
    }
  };

  // Removed: Users are now loaded from context on login, no need to fetch here

  useEffect(() => {
    setShowBulkActions(selectedChecks.size > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChecks]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'COMPLETED': 'status-complete',
      'UNDER_CLARIFICATIONS': 'status-clarification',
      'IN_PROGRESS': 'status-progress',
      'NOT_STARTED': 'status-not-started',
      'OVER_POSTED': 'status-over-posted'
    };
    return statusMap[status] || 'status-not-started';
  };

  const formatCheckType = (checkType) => {
    if (!checkType) return '';
    const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
    return checkTypeItem?.name || checkType;
  };

  const formatStatus = (status) => {
    const statusMap = {
      'COMPLETED': 'Completed',
      // Show shorter label in list view; filters/details keep full text
      'UNDER_CLARIFICATIONS': 'Clarifications',
      'IN_PROGRESS': 'In Progress',
      'NOT_STARTED': 'Not Started',
      'OVER_POSTED': 'Over Posted'
    };
    return statusMap[status] || status;
  };

  // Check if any advanced filter is active
  const hasAdvancedFilters = () => {
    return (
      (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
      (advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0) ||
      (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
      (advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0) ||
      (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
      (advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0) ||
      (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
      (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
      (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) ||
      (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) ||
      (advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0) ||
      (advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0) ||
      (advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0) ||
      (advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0) ||
      (advancedFilters.checkNumber && advancedFilters.checkNumber.trim() !== '') ||
      (advancedFilters.payer && advancedFilters.payer.trim() !== '') ||
      (advancedFilters.batchNumber && advancedFilters.batchNumber.trim() !== '') ||
      (advancedFilters.depositDateFrom && advancedFilters.depositDateFrom.trim() !== '') ||
      (advancedFilters.depositDateTo && advancedFilters.depositDateTo.trim() !== '') ||
      (advancedFilters.receivedDateFrom && advancedFilters.receivedDateFrom.trim() !== '') ||
      (advancedFilters.receivedDateTo && advancedFilters.receivedDateTo.trim() !== '') ||
      (advancedFilters.completedDateFrom && advancedFilters.completedDateFrom.trim() !== '') ||
      (advancedFilters.completedDateTo && advancedFilters.completedDateTo.trim() !== '') ||
      (advancedFilters.startDate && advancedFilters.startDate.trim() !== '') ||
      (advancedFilters.endDate && advancedFilters.endDate.trim() !== '') ||
      advancedFilters.month ||
      advancedFilters.year
    );
  };

  const getDateRangeDisplay = () => {
    // If both start and end dates are selected, show the date range
    if (advancedFilters.startDate && advancedFilters.endDate) {
      return formatDateRange(advancedFilters.startDate, advancedFilters.endDate);
    }
    // If advanced filters are active (but no date range), show custom range
    if (hasAdvancedFilters()) {
      return 'Custom Range';
    }
    // Show month/year format instead of date range
    return formatMonthYear(selectedMonth, selectedYear);
  };


  const totals = checks.reduce((acc, check) => ({
    totalAmount: acc.totalAmount + (check.totalAmount || 0),
    posted: acc.posted + (check.postedAmount || 0),
    remaining: acc.remaining + (check.remainingAmount || 0)
  }), { totalAmount: 0, posted: 0, remaining: 0 });

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="sort-arrow">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className="checks-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Unknown</h1>
          <p className="page-subtitle">Manage and reconcile unknown payment checks</p>
        </div>
        <div className="header-actions">
          {!hasAdvancedFilters() && (
            <div className="date-range-container">
              <button 
                className="date-range-btn"
                onClick={() => setShowMonthPicker(!showMonthPicker)}
              >
                {getDateRangeDisplay()}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '8px' }}>
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showMonthPicker && (
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onSelect={handleMonthYearSelect}
                  onClose={() => setShowMonthPicker(false)}
                />
              )}
            </div>
          )}
          {hasAdvancedFilters() && (
            <div className="date-range-container">
              <div className="date-range-display">
                {getDateRangeDisplay()}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2"/>
            <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by check number (e.g., ch* or ch), payer, amount..."
            value={searchTerm}
            onChange={(e) => {
              const filteredValue = filterEmojis(e.target.value);
              setSearchTerm(filteredValue);
            }}
          />
        </div>
        <div className="filter-dropdowns">
          <SearchableDropdown
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'NOT_STARTED', label: 'Not Started' },
              { value: 'IN_PROGRESS', label: 'In Progress' },
              { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'OVER_POSTED', label: 'Over Posted' }
            ]}
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            placeholder="All Statuses"
            maxVisibleItems={5}
          />
          <SearchableDropdown
            options={[
              { value: '', label: 'All Practices' },
              ...practices.map(practice => ({
                value: practice.code,
                label: practice.name || practice.code
              }))
            ]}
            value={selectedPractice}
            onChange={(value) => setSelectedPractice(value)}
            placeholder="All Practices"
            maxVisibleItems={5}
          />
          <button 
            className={`btn-advanced-filter ${hasAdvancedFilters() ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(true)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M3 5H17M7 10H17M11 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Active Filters Indicator */}
      {(hasAdvancedFilters() || selectedStatus || selectedPractice || searchTerm) && (
        <div className="active-filters-section">
          <div className="active-filters-label">Active Filters:</div>
          <div className="active-filters-chips">
            {searchTerm && (
              <div className="filter-chip">
                <span>Search: {searchTerm}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSearchTerm('')}
                  title="Remove search filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {selectedStatus && (
              <div className="filter-chip">
                <span>Status: {[
                  { value: 'NOT_STARTED', label: 'Not Started' },
                  { value: 'IN_PROGRESS', label: 'In Progress' },
                  { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
                  { value: 'COMPLETED', label: 'Completed' },
                  { value: 'OVER_POSTED', label: 'Over Posted' }
                ].find(s => s.value === selectedStatus)?.label || selectedStatus}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSelectedStatus('')}
                  title="Remove status filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {selectedPractice && (
              <div className="filter-chip">
                <span>Practice: {practices.find(p => p.code === selectedPractice)?.name || selectedPractice}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSelectedPractice('')}
                  title="Remove practice filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {/* Multiple Status Filters */}
            {advancedFilters.statuses && advancedFilters.statuses.length > 0 && advancedFilters.statuses.map((status) => {
              const statusLabels = {
                'NOT_STARTED': 'Not Started',
                'IN_PROGRESS': 'In Progress',
                'UNDER_CLARIFICATIONS': 'Under Clarifications',
                'COMPLETED': 'Completed',
                'OVER_POSTED': 'Over Posted'
              };
              return (
                <div key={status} className="filter-chip">
                  <span>Status: {statusLabels[status] || status}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        statuses: prev.statuses.filter(s => s !== status)
                      }));
                    }}
                    title="Remove status filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0 && advancedFilters.excludeStatuses.map((status) => {
              const statusLabels = {
                'NOT_STARTED': 'Not Started',
                'IN_PROGRESS': 'In Progress',
                'UNDER_CLARIFICATIONS': 'Under Clarifications',
                'COMPLETED': 'Completed',
                'OVER_POSTED': 'Over Posted'
              };
              return (
                <div key={`exclude-${status}`} className="filter-chip filter-chip--exclude">
                  <span>Status: Not {statusLabels[status] || status}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeStatuses: prev.excludeStatuses.filter(s => s !== status) }))} title="Remove excluded status filter">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Practice Filters */}
            {advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0 && advancedFilters.practiceCodes.map((practiceCode) => (
              <div key={practiceCode} className="filter-chip">
                <span>Practice: {practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      practiceCodes: prev.practiceCodes.filter(p => p !== practiceCode),
                      locationCodes: prev.locationCodes.filter(loc => {
                        // Remove locations that belong to this practice
                        const practice = practices.find(p => p.code === practiceCode);
                        if (practice && practice.practiceId) {
                          // This would need location data to check, for now just remove all
                          return false;
                        }
                        return true;
                      })
                    }));
                  }}
                  title="Remove practice filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0 && advancedFilters.excludePracticeCodes.map((practiceCode) => (
              <div key={`exclude-${practiceCode}`} className="filter-chip filter-chip--exclude">
                <span>Practice: Not {practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludePracticeCodes: prev.excludePracticeCodes.filter(p => p !== practiceCode) }))} title="Remove excluded practice filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {/* Multiple Location Filters */}
            {advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0 && advancedFilters.locationCodes.map((locationCode) => (
              <div key={locationCode} className="filter-chip">
                <span>Location: {locationCode}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      locationCodes: prev.locationCodes.filter(l => l !== locationCode)
                    }));
                  }}
                  title="Remove location filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0 && advancedFilters.excludeLocationCodes.map((locationCode) => (
              <div key={`exclude-${locationCode}`} className="filter-chip filter-chip--exclude">
                <span>Location: Not {locationCode}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeLocationCodes: prev.excludeLocationCodes.filter(l => l !== locationCode) }))} title="Remove excluded location filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {/* Multiple Assignee Filters */}
            {advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0 && advancedFilters.assigneeIds.map((assigneeId) => (
              <div key={assigneeId} className="filter-chip">
                <span>Assignee: {getUserName(assigneeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      assigneeIds: prev.assigneeIds.filter(id => id !== assigneeId)
                    }));
                  }}
                  title="Remove assignee filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0 && advancedFilters.excludeAssigneeIds.map((assigneeId) => (
              <div key={`exclude-assignee-${assigneeId}`} className="filter-chip filter-chip--exclude">
                <span>Assignee: Not {getUserName(assigneeId)}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeAssigneeIds: prev.excludeAssigneeIds.filter(id => id !== assigneeId) }))} title="Remove excluded assignee filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {/* Multiple Reporter Filters */}
            {advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0 && advancedFilters.reporterIds.map((reporterId) => (
              <div key={reporterId} className="filter-chip">
                <span>Reporter: {getUserName(reporterId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      reporterIds: prev.reporterIds.filter(id => id !== reporterId)
                    }));
                  }}
                  title="Remove reporter filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0 && advancedFilters.excludeReporterIds.map((reporterId) => (
              <div key={`exclude-reporter-${reporterId}`} className="filter-chip filter-chip--exclude">
                <span>Reporter: Not {getUserName(reporterId)}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeReporterIds: prev.excludeReporterIds.filter(id => id !== reporterId) }))} title="Remove excluded reporter filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {/* Multiple Check Type Filters */}
            {advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0 && advancedFilters.checkTypes.map((checkType) => {
              const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
              const label = checkTypeItem?.name || checkType;
              return (
                <div key={checkType} className="filter-chip">
                  <span>Check Type: {label}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        checkTypes: prev.checkTypes.filter(ct => ct !== checkType)
                      }));
                    }}
                    title="Remove check type filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0 && advancedFilters.excludeCheckTypes.map((checkType) => {
              const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
              const label = checkTypeItem?.name || checkType;
              return (
                <div key={`exclude-${checkType}`} className="filter-chip filter-chip--exclude">
                  <span>Check Type: Not {label}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeCheckTypes: prev.excludeCheckTypes.filter(ct => ct !== checkType) }))} title="Remove excluded check type filter">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Workflow Status Filters */}
            {advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0 && advancedFilters.workflowStatuses.map((workflowStatus) => {
              const workflowStatusItem = workflowStatuses.find(ws => ws.code === workflowStatus);
              const label = workflowStatusItem?.name || workflowStatus;
              return (
                <div key={workflowStatus} className="filter-chip">
                  <span>Workflow Status: {label}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        workflowStatuses: prev.workflowStatuses.filter(ws => ws !== workflowStatus)
                      }));
                    }}
                    title="Remove workflow status filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0 && advancedFilters.excludeWorkflowStatuses.map((workflowStatus) => {
              const workflowStatusItem = workflowStatuses.find(ws => ws.code === workflowStatus);
              const label = workflowStatusItem?.name || workflowStatus;
              return (
                <div key={`exclude-${workflowStatus}`} className="filter-chip filter-chip--exclude">
                  <span>Workflow Status: Not {label}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeWorkflowStatuses: prev.excludeWorkflowStatuses.filter(ws => ws !== workflowStatus) }))} title="Remove excluded workflow status filter">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.checkNumber && (
              <div className="filter-chip">
                <span>Check: {advancedFilters.checkNumber}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, checkNumber: '' }));
                  }}
                  title="Remove check number filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {advancedFilters.payer && (
              <div className="filter-chip">
                <span>Payer: {advancedFilters.payer}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, payer: '' }));
                  }}
                  title="Remove payer filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {advancedFilters.batchNumber && (
              <div className="filter-chip">
                <span>Batch: {advancedFilters.batchNumber}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, batchNumber: '' }));
                  }}
                  title="Remove batch number filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.depositDateFrom || advancedFilters.depositDateTo) && (
              <div className="filter-chip">
                <span>Deposit Date: {formatDateRange(
                  advancedFilters.depositDateFrom || '',
                  advancedFilters.depositDateTo || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, depositDateFrom: '', depositDateTo: '' }));
                  }}
                  title="Remove deposit date filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.receivedDateFrom || advancedFilters.receivedDateTo) && (
              <div className="filter-chip">
                <span>Received Date: {formatDateRange(
                  advancedFilters.receivedDateFrom || '',
                  advancedFilters.receivedDateTo || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, receivedDateFrom: '', receivedDateTo: '' }));
                  }}
                  title="Remove received date filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.completedDateFrom || advancedFilters.completedDateTo) && (
              <div className="filter-chip">
                <span>Completed Date: {formatDateRange(
                  advancedFilters.completedDateFrom || '',
                  advancedFilters.completedDateTo || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, completedDateFrom: '', completedDateTo: '' }));
                  }}
                  title="Remove completed date filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.month || advancedFilters.year) && (
              <div className="filter-chip">
                <span>Period: {formatMonthYear(advancedFilters.month || selectedMonth, advancedFilters.year || selectedYear)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, month: null, year: null }));
                  }}
                  title="Remove period filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            <button
              className="clear-all-filters-btn"
              onClick={handleClearAllFilters}
              title="Clear all filters"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {showBulkActions && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <span>{selectedChecks.size} check(s) selected</span>
          </div>
          <div className="bulk-actions-controls">
            <button className="btn-bulk-update" onClick={() => setShowBulkActionModal(true)}>
              Update
            </button>
            <button className="btn-bulk-cancel" onClick={() => setSelectedChecks(new Set())}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <BulkActionModal
        isOpen={showBulkActionModal}
        onClose={() => setShowBulkActionModal(false)}
        onSave={handleBulkUpdate}
        selectedCount={selectedChecks.size}
        users={users}
        isUnknownPage={true}
      />

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {bulkUpdateMessage && (
        <div className={`bulk-update-message ${bulkUpdateMessageType}`}>
          <div className="message-content">
            <span>{bulkUpdateMessage}</span>
            <button 
              className="message-close-btn"
              onClick={() => setBulkUpdateMessage(null)}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="table-section">
        <div className="table-header">
          <div className="table-header-left">
            {totalElements > 0 && showPagination && (
              <div className="pagination-info">
                <button
                  className="pagination-arrow"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0 || loading}
                  title="Previous page"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <span className="pagination-text">
                  {startIndex} - {endIndex}, of {displayTotal}
                </span>
                <button
                  className="pagination-arrow"
                  onClick={handleNextPage}
                  disabled={!hasMore || loading}
                  title="Next page"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="table-actions">
            <Tooltip text="Reset Column widths" position="top">
              <button
                type="button"
                className="icon-btn"
                onClick={resetColumnWidths}
                aria-label="Reset Column widths"
              >
                <RotateCcw size={18} strokeWidth={2} />
              </button>
            </Tooltip>
            <button 
              ref={columnSelectorTriggerRef}
              className="icon-btn"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="checks-table checks-table--col-resize">
            <Colgroup />
            <thead>
              <tr className="totals-row">
                <th>
                  <Tooltip text="Totals reflect only the rows currently displayed on this page" position="top">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Info 
                        size={16} 
                        style={{ 
                          color: '#374151', 
                          cursor: 'pointer',
                          transition: 'color 0.2s',
                          fontWeight: 'bold',
                          strokeWidth: 2.5,
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                        onMouseLeave={(e) => e.target.style.color = '#374151'}
                      />
                      Totals:
                    </span>
                  </Tooltip>
                </th>
                {tableVisibleColumns.map(colKey => {
                  switch (colKey) {
                    case 'totalAmount':
                      return <th key={colKey}>{formatCurrency(totals.totalAmount)}</th>;
                    case 'postedAmount':
                      return <th key={colKey}>{formatCurrency(totals.posted)}</th>;
                    case 'remainingAmount':
                      return <th key={colKey}>{formatCurrency(totals.remaining)}</th>;
                    default:
                      return <th key={colKey}></th>;
                  }
                })}
              </tr>
              <tr>
                <ResizableTh columnId="__checkbox__" onResizeMouseDown={onResizeMouseDown}>
                  <input 
                    type="checkbox" 
                    checked={selectedChecks.size === checks.length && checks.length > 0}
                    onChange={handleSelectAll}
                  />
                </ResizableTh>
                {tableVisibleColumns.map(colKey => {
                  const column = allColumns.find(col => col.key === colKey);
                  if (!column) return null;

                  switch (colKey) {
                    case 'depositDate':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('depositDate')}
                        >
                          Deposit Date <SortArrow field="depositDate" />
                        </ResizableTh>
                      );
                    case 'checkNumber':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('checkNumber')}
                        >
                          Check Number <SortArrow field="checkNumber" />
                        </ResizableTh>
                      );
                    case 'altCheckNumber':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Alt. Check Number
                        </ResizableTh>
                      );
                    case 'payer':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Payer
                        </ResizableTh>
                      );
                    case 'batchDescription':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Batch Description
                        </ResizableTh>
                      );
                    case 'exchange':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Exchange
                        </ResizableTh>
                      );
                    case 'checkType':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Check Type
                        </ResizableTh>
                      );
                    case 'practiceCode':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Practice Code
                        </ResizableTh>
                      );
                    case 'locationCode':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Location Code
                        </ResizableTh>
                      );
                    case 'totalAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('totalAmount')}
                        >
                          Total Amount <SortArrow field="totalAmount" />
                        </ResizableTh>
                      );
                    case 'postedAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('postedAmount')}
                        >
                          Posted <SortArrow field="postedAmount" />
                        </ResizableTh>
                      );
                    case 'remainingAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('remainingAmount')}
                        >
                          Remaining <SortArrow field="remainingAmount" />
                        </ResizableTh>
                      );
                    case 'status':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Status
                        </ResizableTh>
                      );
                    case 'workflowStatus':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Workflow Status
                        </ResizableTh>
                      );
                    case 'assigneeName':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Assignee Name
                        </ResizableTh>
                      );
                    case 'reporterName':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Reporter Name
                        </ResizableTh>
                      );
                    case 'createdAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('createdAt')}
                        >
                          Created At <SortArrow field="createdAt" />
                        </ResizableTh>
                      );
                    case 'updatedAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('updatedAt')}
                        >
                          Updated At <SortArrow field="updatedAt" />
                        </ResizableTh>
                      );
                    default:
                      return null;
                  }
                })}
              </tr>
            </thead>
            <tbody>
              {loading && checks.length === 0 ? (
                <tr>
                  <td colSpan={tableVisibleColumns.length + 1} style={{ textAlign: 'center', padding: '40px' }}>
                    Loading unknown checks...
                  </td>
                </tr>
              ) : checks.length === 0 ? (
                <tr>
                  <td colSpan={tableVisibleColumns.length + 1} style={{ textAlign: 'center', padding: '40px' }}>
                    No unknown checks found
                  </td>
                </tr>
              ) : (
                checks.map((check) => (
                  <tr
                    key={check.checkId}
                    className="checks-table-row--clickable"
                    onClick={() => goToUnknownCheckDetails(check)}
                  >
                    <td className="checkbox-cell" onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={selectedChecks.has(check.checkId)}
                        onChange={() => handleSelectCheck(check.checkId)}
                      />
                    </td>
                    {tableVisibleColumns.map(colKey => {
                      switch (colKey) {
                        case 'depositDate':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={check.depositDate ? formatDateUS(check.depositDate) : ''} />
                            </td>
                          );
                        case 'checkNumber':
                          return (
                            <td key={colKey} className="check-number-table-cell">
                              {check.checkNumber ? (
                                <div className="check-number-cell-inner">
                                  <button
                                    type="button"
                                    className="checks-check-number-copy-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyCheckNumber(check.checkNumber);
                                    }}
                                    title="Copy check number"
                                    aria-label="Copy check number"
                                  >
                                    <Copy size={16} aria-hidden />
                                  </button>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <CellEllipsis text={check.checkNumber} />
                                  </div>
                                </div>
                              ) : (
                                <CellEllipsis text="" />
                              )}
                            </td>
                          );
                        case 'altCheckNumber':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={check.altCheckNumber || ''} />
                            </td>
                          );
                        case 'payer':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={check.payer || ''} />
                            </td>
                          );
                        case 'batchDescription':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={check.batchDescription || ''} />
                            </td>
                          );
                        case 'exchange':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={check.exchange || ''} />
                            </td>
                          );
                        case 'checkType':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCheckType(check.checkType)} />
                            </td>
                          );
                        case 'practiceCode':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={check.practiceCode || ''} />
                            </td>
                          );
                        case 'locationCode':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={check.locationCode || ''} />
                            </td>
                          );
                        case 'totalAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(check.totalAmount)} />
                            </td>
                          );
                        case 'postedAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(check.postedAmount)} />
                            </td>
                          );
                        case 'remainingAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(check.remainingAmount)} />
                            </td>
                          );
                        case 'status':
                          return (
                            <td key={colKey}>
                              <span className={`status-badge ${getStatusClass(check.status)}`}>
                                <CellEllipsis text={formatStatus(check.status)} />
                              </span>
                            </td>
                          );
                        case 'workflowStatus':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={check.workflowStatus || 'N/A'} />
                            </td>
                          );
                        case 'assigneeName':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={getUserName(check.assigneeId)} />
                            </td>
                          );
                        case 'reporterName':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={getUserName(check.reporterId)} />
                            </td>
                          );
                        case 'createdAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp
                                userId={check.createdBy}
                                dateTime={check.createdAt}
                                action="created"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                          );
                        case 'updatedAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp
                                userId={check.updatedBy}
                                dateTime={check.updatedAt}
                                action="updated"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                          );
                        default:
                          return null;
                      }
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      <AdvancedFilterDrawer
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        filters={advancedFilters}
        onApplyFilters={handleApplyAdvancedFilters}
        onResetFilters={handleResetAdvancedFilters}
        isUnknownPage={true}
      />

      {showColumnSelector && (
        <ColumnSelector
          availableColumns={allColumns.filter((c) => c.key !== 'checkbox')}
          visibleColumns={tableVisibleColumns}
          defaultColumns={defaultColumns}
          onChange={(cols) => {
            const next = cols.filter((c) => c !== 'checkbox');
            setVisibleColumns(next);
            persistVisibleColumns(UNKNOWN_CHECKS_COLUMN_PREFS_KEY, next);
          }}
          onClose={() => setShowColumnSelector(false)}
          triggerRef={columnSelectorTriggerRef}
        />
      )}
    </div>
  );
};

export default Unknown;
